import { Badge, Box, Link, Select, Text, TextArea, Button, Modal, Icon, HStack, VStack, Spinner, useColorModeValue, useToast } from 'native-base';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Auth } from 'aws-amplify';
import { IStore } from 'src/redux/reducers';
import { IProductRelease } from 'src/models';
import { PRIVATE_ALGO_PRODUCT_IDS } from 'src/constants';
import * as ProductReleaseAction from '../../redux/actions/productReleaseAction';
import DisplayCsvTableComponent from 'src/components/DisplayCsvTableComponent';
import { containerStyle } from 'src/styles/containerStyle';
import { generateUploadUrl, confirmEncryptedUpload } from '../../api/productReleases';

const isPrivateAlgoProduct = (productId: string): boolean => PRIVATE_ALGO_PRODUCT_IDS.has(productId);

export default function ProductReleasePage() {

    const dispatch = useDispatch();
    const productReleases = useSelector((store: IStore) => store.productRelease.productReleases);
    const toast = useToast();
    const cardBg = useColorModeValue('white', 'coolGray.800');
    const cardBorder = useColorModeValue('coolGray.200', 'coolGray.700');
    const mutedText = useColorModeValue('coolGray.600', 'coolGray.300');
    const pillBorder = useColorModeValue('coolGray.300', 'coolGray.600');
    const pillHoverBg = useColorModeValue('coolGray.100', 'coolGray.700');
    const latestCardBg = useColorModeValue('coolGray.50', 'coolGray.900');

    const [selectedProduct, setSelectedProduct] = useState('m10');
    const [productReleaseData, setProductReleaseData] = useState<React.ReactNode[][]>([]);
    const [editingRelease, setEditingRelease] = useState<{ releaseId: string; changeLog: string } | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [selectedUpload, setSelectedUpload] = useState<{
        releaseId: string;
        platform: string;
        moduleName: string;
    } | null>(null);

    const [columnsToDisplay, setColumnsToDisplay] = useState<number[]>([0, 1, 2, 3]);
    const sortedReleases = React.useMemo(() => {
        const releases: IProductRelease[] = productReleases?.response ?? [];
        return [...releases].sort((a, b) => b.createdAt - a.createdAt);
    }, [productReleases?.response]);
    const latestRelease = sortedReleases?.[0];

    const pillIconColor = useColorModeValue('coolGray.600', 'coolGray.300');
    const pillTextColor = useColorModeValue('coolGray.800', 'coolGray.100');

    const renderLinkPill = (href: string, label: string, iconName: string, tone?: 'success' | 'muted') => (
        <Link
            isExternal
            href={href}
            isUnderlined={false}
            px={2}
            py={1}
            borderWidth={1}
            borderColor={pillBorder}
            rounded="sm"
            _hover={{ bg: pillHoverBg }}
        >
            <HStack space={1} alignItems="center">
                <Icon
                    as={MaterialCommunityIcons}
                    name={iconName}
                    size="xs"
                    color={tone === 'success' ? 'success.500' : pillIconColor}
                />
                <Text fontSize="xs" fontWeight="600" color={pillTextColor}>
                    {label}
                </Text>
            </HStack>
        </Link>
    );

    type AlgoPlatforms = Record<
        string,
        {
            available?: boolean;
            modules?: Record<string, { raw?: string; encrypted?: string }>;
        }
    >;

    const renderAlgoPlatforms = (releaseId: string, platforms: AlgoPlatforms | undefined, options?: { dense?: boolean }) => {
        const dense = options?.dense ?? false;
        const stackSpace = dense ? 2 : 3;
        const cardPadding = dense ? 2 : 3;
        const moduleSpacing = dense ? 2 : 3;
        const platformKeys = Object.keys(platforms || {});

        if (platformKeys.length === 0) {
            return (
                <Text fontSize="sm" color={mutedText}>
                    No platform artifacts available
                </Text>
            );
        }

        return (
            <VStack space={stackSpace}>
                {platformKeys.map((platform) => {
                    const platformData = platforms?.[platform] || {};
                    const modules = platformData?.modules || {};
                    const moduleNames = Object.keys(modules);

                    return (
                        <Box
                            key={platform}
                            borderWidth={1}
                            borderColor={cardBorder}
                            bg={cardBg}
                            rounded="md"
                            p={cardPadding}
                        >
                            <HStack alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap">
                                <Badge variant="subtle" colorScheme="coolGray">
                                    {platform}
                                </Badge>
                                <Text fontSize="xs" color={mutedText}>
                                    {moduleNames.length} module{moduleNames.length === 1 ? '' : 's'}
                                </Text>
                            </HStack>

                            <VStack space={moduleSpacing}>
                                {moduleNames.length === 0 ? (
                                    <Text fontSize="sm" color={mutedText}>
                                        No modules available
                                    </Text>
                                ) : (
                                    moduleNames.map((moduleName) => {
                                        const module = modules?.[moduleName] || {};
                                        const hasEncrypted = Boolean(module.encrypted);

                                        return (
                                            <HStack
                                                key={`${platform}:${moduleName}`}
                                                alignItems="center"
                                                justifyContent="space-between"
                                                flexWrap="wrap"
                                                space={2}
                                            >
                                                <HStack space={2} alignItems="center" flexShrink={1} minW="220px">
                                                    <Icon
                                                        as={MaterialCommunityIcons}
                                                        name={hasEncrypted ? 'check-circle' : 'clock-outline'}
                                                        size="sm"
                                                        color={hasEncrypted ? 'success.500' : 'warning.500'}
                                                    />
                                                    <Text fontSize="sm" fontWeight="600" flexShrink={1}>
                                                        {moduleName}
                                                    </Text>
                                                </HStack>

                                                <HStack alignItems="center" space={2} flexWrap="wrap">
                                                    {module.raw && renderLinkPill(module.raw, 'Raw', 'download-outline')}
                                                    {module.encrypted && renderLinkPill(module.encrypted, 'Encrypted', 'lock-check-outline', 'success')}
                                                    {!hasEncrypted && (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="warning"
                                                            variant="solid"
                                                            onPress={() => handleUploadEncrypted(releaseId, platform, moduleName)}
                                                            leftIcon={<Icon as={MaterialCommunityIcons} name="cloud-upload-outline" size="sm" />}
                                                        >
                                                            Upload .enc
                                                        </Button>
                                                    )}
                                                </HStack>
                                            </HStack>
                                        );
                                    })
                                )}
                            </VStack>
                        </Box>
                    );
                })}
            </VStack>
        );
    };

    // Check authentication status on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                await Auth.currentSession();
                setIsAuthenticated(true);
            } catch {
                setIsAuthenticated(false);
            }
        };
        checkAuth();
    }, []);

    const handleProductChange = (itemValue: string) => {
        setSelectedProduct(itemValue);
        const requireAuth = isPrivateAlgoProduct(itemValue);
        dispatch(ProductReleaseAction.getProductReleases.request({ productId: itemValue, requireAuth }));
    }

    useEffect(() => {
        const requireAuth = isPrivateAlgoProduct(selectedProduct);
        dispatch(ProductReleaseAction.getProductReleases.request({ productId: selectedProduct, requireAuth }));
    }, []);

    const handleEditChangeLog = (releaseId: string, currentChangeLog?: string) => {
        setEditingRelease({ releaseId, changeLog: currentChangeLog || '' });
        setIsModalOpen(true);
    };

    const handleSaveChangeLog = () => {
        if (editingRelease) {
            dispatch(ProductReleaseAction.updateProductReleaseChangeLog.request({
                productId: selectedProduct,
                releaseId: editingRelease.releaseId,
                changeLog: editingRelease.changeLog
            }));
            setIsModalOpen(false);
            setEditingRelease(null);
        }
    };

    const handleCancelEdit = () => {
        setIsModalOpen(false);
        setEditingRelease(null);
    };

    const handleUploadEncrypted = (releaseId: string, platform: string, moduleName: string) => {
        setSelectedUpload({ releaseId, platform, moduleName });
        setUploadModalOpen(true);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedUpload) return;

        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingFile(true);
        try {
            // Step 1: Get presigned URL
            const { uploadUrl, fileKey } = await generateUploadUrl(
                selectedProduct,
                selectedUpload.releaseId,
                selectedUpload.platform,
                selectedUpload.moduleName
            );

            // Step 2: Upload file to S3
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to S3');
            }

            // Step 3: Confirm upload with backend
            await confirmEncryptedUpload(
                selectedProduct,
                selectedUpload.releaseId,
                selectedUpload.platform,
                selectedUpload.moduleName,
                fileKey
            );

            toast.show({
                description: 'Encrypted module uploaded successfully!',
                status: 'success',
            });

            // Refresh releases
            dispatch(ProductReleaseAction.getProductReleases.request({
                productId: selectedProduct,
                requireAuth: isPrivateAlgoProduct(selectedProduct)
            }));

            setUploadModalOpen(false);
            setSelectedUpload(null);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('Upload error:', error);
            toast.show({
                description: `Upload failed: ${message}`,
                status: 'error',
            });
        } finally {
            setUploadingFile(false);
        }
    };

    const formatReleaseDate = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        if (productReleases?.response && productReleases?.response?.length > 0) {
            // Sort by timestamp first
            const sorted = [...productReleases.response].sort((a, b) => b.createdAt - a.createdAt);

            const sortedProductReleaseData: React.ReactNode[][] = sorted.map((productRelease) => {
                const formattedDate = formatReleaseDate(productRelease?.createdAt);
                const changeLogCell = productRelease.changeLog ? (
                    <HStack alignItems="center" space={2}>
                        <Text flex={1}>{productRelease.changeLog}</Text>
                        <Button
                            size="xs"
                            variant="ghost"
                            onPress={() => handleEditChangeLog(productRelease.releaseId, productRelease.changeLog)}
                            leftIcon={<Icon as={MaterialCommunityIcons} name="pencil" size="sm" />}
                        />
                    </HStack>
                ) : (
                    <Button size="sm" onPress={() => handleEditChangeLog(productRelease.releaseId, productRelease.changeLog)}>
                        Add ChangeLog
                    </Button>
                );

                // Handle private algo releases differently
                if (isPrivateAlgoProduct(selectedProduct) && productRelease.metadata?.platforms) {
                    const platforms = productRelease.metadata.platforms;
                    const platformsDisplay = (
                        renderAlgoPlatforms(productRelease.releaseId, platforms, { dense: true })
                    );

                    return [
                        productRelease?.releaseId.substring(0, 8),
                        formattedDate,
                        productRelease?.version,
                        platformsDisplay,
                        changeLogCell
                    ];
                }

                // Handle other products
                const urlText = selectedProduct === 'web' ? (
                    <Link isExternal href={productRelease.url} isUnderlined={false} _text={{ fontWeight: '600' }}>
                        Go to Web App
                    </Link>
                ) : selectedProduct === 'm10' ? (
                    <Link isExternal href={productRelease.url} isUnderlined={false} _text={{ fontWeight: '600' }}>
                        Download app
                    </Link>
                ) : (
                    <Link isExternal href={productRelease.url} isUnderlined={false} _text={{ fontWeight: '600' }}>
                        Download installer
                    </Link>
                );

                return [
                    productRelease?.releaseId.substring(0, 8),
                    formattedDate,
                    productRelease?.version,
                    urlText,
                    changeLogCell
                ]
            });

            if (selectedProduct === 'web') {
                setColumnsToDisplay([0, 1, 2, 4]);
            } else {
                setColumnsToDisplay([0, 1, 2, 3, 4]);
            }
            setProductReleaseData(sortedProductReleaseData);
        }
    }, [productReleases, selectedProduct]);

    return (
        <Box variant={"main"} style={containerStyle.mainContainer}>
            <Select
                selectedValue={selectedProduct}
                onValueChange={itemValue => handleProductChange(itemValue)}
                maxW="420px"
                w="100%"
                mb={4}
            >
                <Select.Item label="M10" value="m10" />
                <Select.Item label="W10" value="w10" />
                <Select.Item label="W10 Linux" value="w10_linux" />
                <Select.Item label="W10 MacOS" value="w10_mac" />
                <Select.Item label="Web" value="web" />
                {isAuthenticated && <Select.Item label="Algo L10 (Private)" value="algo_l10" />}
                {isAuthenticated && <Select.Item label="Algo M10 (Private)" value="algo_m10" />}
                {isAuthenticated && <Select.Item label="Algo W10 (Private)" value="algo_w10" />}
            </Select>

            <Box
                borderWidth={1}
                borderColor={cardBorder}
                bg={latestCardBg}
                rounded="lg"
                p={4}
                mb={2}
            >
                <HStack alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap">
                    <Text fontSize="lg" fontWeight="700">
                        Latest Release
                    </Text>
                    {isPrivateAlgoProduct(selectedProduct) && (
                        <Badge colorScheme="warning" variant="subtle">
                            Private
                        </Badge>
                    )}
                </HStack>

                {productReleaseData?.length > 0 ? (
                    <VStack space={3}>
                        <HStack space={2} alignItems="baseline" flexWrap="wrap">
                            <Text color={mutedText} minW="120px">
                                Version
                            </Text>
                            <Text fontWeight="700">{productReleaseData[0][2]}</Text>
                        </HStack>

                        <HStack space={2} alignItems="baseline" flexWrap="wrap">
                            <Text color={mutedText} minW="120px">
                                Released
                            </Text>
                            <Text>{productReleaseData[0][1]}</Text>
                        </HStack>

                        <Box>
                            <Text color={mutedText} mb={2}>
                                Download
                            </Text>
                            {isPrivateAlgoProduct(selectedProduct) ? (
                                renderAlgoPlatforms(latestRelease?.releaseId || '', latestRelease?.metadata?.platforms || {})
                            ) : (
                                <Box>{productReleaseData[0][3]}</Box>
                            )}
                        </Box>
                    </VStack>
                ) : (
                    <Text color={mutedText}>No releases found.</Text>
                )}
            </Box>

            <DisplayCsvTableComponent
                title="Product Release History"
                columns={columnsToDisplay}
                tableHead={["Release Id", "Release Date", "Version", "URL", "ChangeLog"]}
                tableData={productReleaseData} />

            <Modal isOpen={isModalOpen} onClose={handleCancelEdit}>
                <Modal.Content maxWidth="600px">
                    <Modal.CloseButton />
                    <Modal.Header>Edit ChangeLog</Modal.Header>
                    <Modal.Body>
                        <Text mb={2}>Release ID: {editingRelease?.releaseId.substring(0, 8)}</Text>
                        <TextArea
                            value={editingRelease?.changeLog || ''}
                            onChangeText={(text) => setEditingRelease(prev => prev ? { ...prev, changeLog: text } : null)}
                            placeholder="Enter changelog notes..."
                            h={200}
                            autoCompleteType={undefined}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={2}>
                            <Button variant="ghost" onPress={handleCancelEdit}>
                                Cancel
                            </Button>
                            <Button onPress={handleSaveChangeLog}>
                                Save
                            </Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>

            <Modal isOpen={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
                <Modal.Content maxWidth="600px">
                    <Modal.CloseButton />
                    <Modal.Header>Upload Encrypted Module</Modal.Header>
                    <Modal.Body>
                        {selectedUpload && (
                            <VStack space={3}>
                                <Text>Release ID: {selectedUpload.releaseId.substring(0, 8)}</Text>
                                <Text>Platform: {selectedUpload.platform}</Text>
                                <Text>Module: {selectedUpload.moduleName}</Text>
                                <input
                                    type="file"
                                    accept=".pyd,.so,.enc"
                                    onChange={handleFileUpload}
                                    disabled={uploadingFile}
                                />
                                {uploadingFile && (
                                    <HStack space={2} alignItems="center">
                                        <Spinner />
                                        <Text>Uploading...</Text>
                                    </HStack>
                                )}
                            </VStack>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" onPress={() => setUploadModalOpen(false)} isDisabled={uploadingFile}>
                            Cancel
                        </Button>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
        </Box>
    );
}
